import React, { useState } from 'react';
import { ChevronRight, Video, RotateCcw, Youtube, PlayCircle, ExternalLink } from 'lucide-react';

interface ScheduleBookLectureProps {
    lectureSets: any[];
    onPlayVideo?: (url: string, title: string) => void;
}

const ScheduleBookLecture: React.FC<ScheduleBookLectureProps> = ({ lectureSets, onPlayVideo }) => {
    const [selectedLectureSetId, setSelectedLectureSetId] = useState<string | null>(null);

    return (
        <div className="space-y-3">
            {!selectedLectureSetId ? (
                lectureSets.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {lectureSets.map((set, idx) => (
                            <button
                                key={idx}
                                onClick={() => setSelectedLectureSetId(set.id)}
                                className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-transparent hover:border-indigo-200 hover:bg-white transition-all text-left group"
                            >
                                <div className="flex flex-col">
                                    <h5 className="text-xs font-black text-slate-800">{set.title}</h5>
                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{(set.lectures || []).length} 강의</span>
                                </div>
                                <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-indigo-500 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                    <ChevronRight size={16} />
                                </div>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="py-12 text-center bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-100">
                        <Video size={32} className="mx-auto text-slate-200 mb-2" />
                        <p className="text-xs font-bold text-slate-400">등록된 인강 정보가 없습니다.</p>
                    </div>
                )
            ) : (
                <div className="animate-in fade-in zoom-in-95 duration-300">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={() => setSelectedLectureSetId(null)}
                            className="flex items-center gap-1.5 text-[10px] font-black text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <RotateCcw size={12} /> 목록으로 돌아가기
                        </button>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-slate-800">{lectureSets.find(s => s.id === selectedLectureSetId)?.title}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {lectureSets.find(s => s.id === selectedLectureSetId)?.lectures.map((lec: any, idx: number) => {
                            const isYoutube = lec.link?.includes('youtube.com') || lec.link?.includes('youtu.be');
                            const isVimeo = lec.link?.includes('vimeo.com');

                            return (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        if (isYoutube || isVimeo) {
                                            onPlayVideo?.(lec.link, lec.title);
                                        } else {
                                            let targetUrl = lec.link;
                                            if (!targetUrl.startsWith('http://') && !targetUrl.startsWith('https://')) {
                                                targetUrl = `https://${targetUrl}`;
                                            }
                                            window.open(targetUrl, '_blank', 'noreferrer');
                                        }
                                    }}
                                    className="flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-transparent hover:border-indigo-200 hover:bg-white transition-all group w-full text-left"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className={`w-10 h-10 rounded-xl shadow-sm flex items-center justify-center transition-all shrink-0 ${isYoutube ? 'bg-red-50 text-red-600 group-hover:bg-red-600 group-hover:text-white' :
                                            isVimeo ? 'bg-sky-50 text-sky-600 group-hover:bg-sky-600 group-hover:text-white' :
                                                'bg-white text-indigo-500 group-hover:bg-indigo-600 group-hover:text-white'
                                            }`}>
                                            {isYoutube ? <Youtube size={18} /> :
                                                isVimeo ? <Video size={18} /> :
                                                    <Video size={18} />}
                                        </div>
                                        <div className="min-w-0">
                                            <h5 className="text-xs font-black text-slate-800 truncate">{lec.title}</h5>
                                            <p className="text-[9px] text-slate-400 font-bold truncate">{lec.link}</p>
                                        </div>
                                    </div>
                                    {isYoutube || isVimeo ? (
                                        <PlayCircle size={18} className={`ml-2 shrink-0 ${isYoutube ? 'text-red-400 group-hover:text-red-500' :
                                            isVimeo ? 'text-sky-400 group-hover:text-sky-500' :
                                                'text-slate-300'
                                            }`} />
                                    ) : (
                                        <ExternalLink size={14} className="text-slate-300 group-hover:text-indigo-500 ml-2 shrink-0" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScheduleBookLecture;
